import { useMemo, useState } from 'react';
import { closeInterface } from '../../services/InterfaceService';
import { UI_FORGE_GRID_FRAME_REGION } from '../../data/ui.atlas';
import { useLoadoutStore } from '../../store/loadoutSlice';
import { useSpellCatalogStore } from '../../store/spellCatalogueSlice';
import { useInventoryStore } from '../../store/inventorySlice';
import { useSocketsStore } from '../../store/socketsSlice';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../../data/trinkets.config';
import { ITEMS } from '../../data/inventory.config';
import { MODIFIER_REGISTRY } from '../../data/modifiers.config';
import { SKETCH_REGISTRY } from '../../data/sketches.config';
import { SPELL_REGISTRY } from '../../data/spells.config';
import { ItemType } from '../../types/inventory.types';
import * as TrinketService from '../../services/TrinketService';
import * as SketchService from '../../services/SketchService';
import { SocketService } from '../../services/SocketService';
import { describeSketch } from '../../services/TrinketDescriptionService';
import {
  describeSpell,
  trinketCard,
  cardToData,
  type DescLine,
  type SpellStat,
  type Card,
} from '../../services/CardDescriptionService';
import { useLoadoutBoardData } from '../../hooks/useLoadoutBoardData';
import { LoadoutBoard, type SlotRef, type BoardRef } from './LoadoutBoard';
import { ItemList, ItemListEntry } from './ItemList';
import { KeyDisplay } from './KeyDisplay';
import { NineSliceFrame } from './NineSliceFrame';
import './LoadoutScreen.scss';

const PICKER_COLS = 5;
const PICKER_ROWS = 4;

interface PickItem {
  key: string;
  icon?: string;
  name: string;
  subtitle?: string;
  lines?: DescLine[];
  stats?: SpellStat[];
  rootCard?: Card;
}

// Picker category from the focused board cell. A socket (or nothing) maps to
// no category → the picker stays empty (sockets are filled from the right only
// in socketing mode, with boosts — not from this category picker).
function slotCategory(ref: BoardRef | null): SlotRef | null {
  if (!ref || ref.kind === 'socket') {
    return null;
  }
  return ref;
}

export const LoadoutScreen = () => {
  const [picking, setPicking] = useState<SlotRef | null>(null);
  const [socketing, setSocketing] = useState<{
    minorId: string;
    socketIndex: number;
  } | null>(null);
  const [hovered, setHovered] = useState<BoardRef | null>(null);
  const [boardFocus, setBoardFocus] = useState<BoardRef | null>(null);

  const equipped = useLoadoutStore((s) => s.equipped);
  const majorTrinkets = useLoadoutStore((s) => s.majorTrinkets);
  const minorTrinkets = useLoadoutStore((s) => s.minorTrinkets);
  const sketches = useLoadoutStore((s) => s.sketches);
  const unlockedSpells = useSpellCatalogStore((s) => s.unlockedSpells);
  const inventorySlots = useInventoryStore((s) => s.slots);
  const removeItem = useInventoryStore((s) => s.removeItem);
  const socketed = useSocketsStore((s) => s.socketed);

  const pickerActive = picking !== null || socketing !== null;

  const { slots, sockets, trinketLines, getCard } = useLoadoutBoardData();

  // Picker category follows the slot being edited, else the hovered slot.
  const category = slotCategory(picking ?? hovered);

  const pickerItems = useMemo<PickItem[]>(() => {
    if (!category) {
      return [];
    }
    if (category.kind === 'major') {
      return Object.entries(majorTrinkets)
        .filter(([modelId]) => modelId !== equipped.major)
        .map(([modelId, level]) => {
          const def = TRINKET_MAJOR_REGISTRY[modelId];
          const card = def ? trinketCard(modelId, level, true) : null;
          return {
            key: `trinket:${modelId}`,
            icon: def?.icon,
            name: def?.name ?? modelId,
            subtitle: `Niveau ${level}`,
            ...(card ? cardToData(card) : { lines: trinketLines(modelId) }),
          };
        });
    }
    if (category.kind === 'minor') {
      const equippedMinorIds = new Set(
        equipped.slots
          .map((s) => s.minorId)
          .filter((id): id is string => id !== null),
      );
      const result: PickItem[] = [];
      for (const [modelId, level] of Object.entries(minorTrinkets)) {
        if (equippedMinorIds.has(modelId)) {
          continue;
        }
        const def = TRINKET_MINOR_REGISTRY[modelId];
        if (!def) {
          continue;
        }
        const card = trinketCard(modelId, level, false);
        result.push({
          key: `trinket:${modelId}`,
          icon: def.icon,
          name: def.name,
          subtitle: `Niveau ${level}`,
          lines: trinketLines(modelId),
          ...(card ? { stats: card.stats, rootCard: card } : {}),
        });
      }
      return result;
    }
    if (category.kind === 'spell') {
      // Filter out every equipped spell (including the hovered slot's own) —
      // an already-equipped spell shouldn't reappear in the picker.
      const equippedSpells = new Set(
        equipped.slots
          .map((s) => s.spellId)
          .filter((id): id is string => id !== null),
      );
      return unlockedSpells
        .filter((spellId) => !equippedSpells.has(spellId))
        .map((spellId) => {
          const def = SPELL_REGISTRY[spellId];
          return {
            key: `spell:${spellId}`,
            icon: def?.icon,
            name: def?.name ?? spellId,
            ...(def ? cardToData(describeSpell(def)) : {}),
          };
        });
    }
    const targetSlot: 'sketch1' | 'sketch2' =
      category.index === 0 ? 'sketch1' : 'sketch2';
    const equippedSketchIds = new Set(
      [equipped.sketches.sketch1, equipped.sketches.sketch2].filter(
        (id): id is string => id !== null,
      ),
    );
    const result: PickItem[] = [];
    for (const modelId of sketches) {
      if (equippedSketchIds.has(modelId)) {
        continue;
      }
      const def = SKETCH_REGISTRY[modelId];
      if (
        !def ||
        !SketchService.canEquipSketchElement(targetSlot, def.emotion)
      ) {
        continue;
      }
      result.push({
        key: `sketch:${modelId}`,
        icon: def.icon,
        name: def.name,
        subtitle: def.emotion,
        lines: describeSketch(def),
      });
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    category,
    equipped,
    majorTrinkets,
    minorTrinkets,
    sketches,
    unlockedSpells,
    socketed,
  ]);

  const boostPickerItems = useMemo<PickItem[]>(() => {
    if (!socketing) {
      return [];
    }
    const result: PickItem[] = [];
    for (const slot of inventorySlots) {
      const def = slot ? ITEMS[slot.itemId] : null;
      if (!def || def.type !== ItemType.BOOST || !def.modifierId) {
        continue;
      }
      const mod = MODIFIER_REGISTRY[def.modifierId];
      result.push({
        key: slot!.itemId,
        icon: def.sprite,
        name: def.label,
        subtitle: mod?.displayTemplate,
      });
    }
    return result;
  }, [socketing, inventorySlots]);

  const activePickItems = socketing ? boostPickerItems : pickerItems;

  const listItems: ItemListEntry[] = activePickItems.map((it) => ({
    id: it.key,
    navId: `pick:${it.key}`,
    icon: it.icon,
    card: {
      icon: it.icon,
      name: it.name,
      subtitle: it.subtitle,
      lines: it.lines,
      stats: it.stats,
      rootCard: it.rootCard,
    },
  }));

  const equip = (slot: SlotRef, itemKey: string) => {
    if (slot.kind === 'major' && itemKey.startsWith('trinket:')) {
      TrinketService.equipMajor(itemKey.slice('trinket:'.length));
    } else if (slot.kind === 'spell' && itemKey.startsWith('spell:')) {
      TrinketService.setSpellInSlot(slot.index, itemKey.slice('spell:'.length));
    } else if (slot.kind === 'minor' && itemKey.startsWith('trinket:')) {
      TrinketService.equipMinorInSlot(
        slot.index,
        itemKey.slice('trinket:'.length),
      );
    } else if (slot.kind === 'sketch' && itemKey.startsWith('sketch:')) {
      SketchService.equipSketch(
        slot.index === 0 ? 'sketch1' : 'sketch2',
        itemKey.slice('sketch:'.length),
      );
    }
  };

  const onBoardActivate = (ref: BoardRef) => {
    if (ref.kind === 'socket') {
      const socketedIds = SocketService.getSockets(ref.minorId);
      if (
        socketedIds[ref.socketIndex] == null &&
        SocketService.canSocket(ref.minorId)
      ) {
        setSocketing({
          minorId: ref.minorId,
          socketIndex: ref.socketIndex,
        });
      }
      return;
    }
    // Enter pick mode only when the slot has equippable choices (the picker
    // already reflects the hovered slot's category).
    if (pickerItems.length > 0) {
      setPicking(ref);
    }
  };

  const onBoardSecondary = (ref: BoardRef) => {
    if (ref.kind === 'socket') {
      const modifierId = SocketService.getSockets(ref.minorId)[ref.socketIndex];
      if (modifierId) {
        SocketService.unsocketBoost(ref.minorId, modifierId);
      }
      return;
    }
    switch (ref.kind) {
      case 'major':
        TrinketService.unequipMajor();
        break;
      case 'minor':
        TrinketService.unequipMinorFromSlot(ref.index);
        break;
      case 'spell':
        TrinketService.setSpellInSlot(ref.index, null);
        break;
      case 'sketch':
        SketchService.unequipSketch(ref.index === 0 ? 'sketch1' : 'sketch2');
        break;
    }
  };

  const onPickConfirm = (item: ItemListEntry) => {
    const key = item.navId.slice('pick:'.length);
    if (socketing) {
      const def = ITEMS[key];
      if (def?.modifierId) {
        if (
          SocketService.socketBoost(
            socketing.minorId,
            socketing.socketIndex,
            def.modifierId,
          )
        ) {
          removeItem(key, 1);
        }
      }
      const target: BoardRef = {
        kind: 'socket',
        minorId: socketing.minorId,
        socketIndex: socketing.socketIndex,
      };
      setSocketing(null);
      setBoardFocus(target);
      return;
    }
    if (picking) {
      equip(picking, key);
      const target = picking;
      setPicking(null);
      setBoardFocus(target);
    }
  };

  const onPickCancel = () => {
    const target: BoardRef | null = socketing
      ? {
          kind: 'socket',
          minorId: socketing.minorId,
          socketIndex: socketing.socketIndex,
        }
      : picking;
    setSocketing(null);
    setPicking(null);
    setBoardFocus(target);
  };

  return (
    <div className="loadout-overlay">
      <div className="loadout-frame">
        <NineSliceFrame className="loadout-board-frame">
          <div className="loadout-page-content">
            <LoadoutBoard
              active={!pickerActive}
              slots={slots}
              sockets={sockets}
              getCard={getCard}
              focusTarget={!pickerActive ? boardFocus : null}
              onActivate={onBoardActivate}
              onSecondary={onBoardSecondary}
              onFocusChange={setHovered}
              onCancel={closeInterface}
            />
          </div>
        </NineSliceFrame>
        <NineSliceFrame
          region={UI_FORGE_GRID_FRAME_REGION}
          className="loadout-picker-frame"
        >
          <div className="loadout-page-content">
            {socketing && (
              <div className="loadout-socket-hint">Boost à poser</div>
            )}
            <ItemList
              active={pickerActive}
              items={listItems}
              cols={PICKER_COLS}
              maxRows={PICKER_ROWS}
              onConfirm={onPickConfirm}
              onCancel={onPickCancel}
            />
          </div>
        </NineSliceFrame>
      </div>
      <div className="loadout-hint">
        <KeyDisplay context="interface" action="DETAILS" size="sm" /> détails
        &nbsp;·&nbsp;{' '}
        <KeyDisplay context="interface" action="SECONDARY" size="sm" />{' '}
        {pickerActive ? 'annuler' : 'déséquiper'} &nbsp;·&nbsp;{' '}
        <KeyDisplay context="interface" action="CANCEL" size="sm" /> retour
      </div>
    </div>
  );
};
